<template>
  <div v-if="model" class="navbar navbar-default navbar-fixed-top">
    <TeamPickerDialog
      ref="teamPickerDialog"
      :teams="model.teams"
      :can-create-teams="canCreateTeams"
    />
    <div class="container">
      <button
        @click="$emit('open-side-nav')"
        class="container side-nav-toggle-btn"
        aria-label="burger menu"
        type="button"
      >
        <div class="burger-menu"></div>
      </button>
      <div class="navbar-header">
        <ul class="nav navbar-nav">
          <li class="team-button-container">
            <a
              class="btn btn-default"
              :style="{ borderRadius: !!model.teams.length ? '20px 0 0 20px' : '20px' }"
              href="/team/create"
              >Setup a new team</a
            >
          </li>
          <li v-if="currentTeam" class="team-button-container">
            <a
              href="javascript: void(0);"
              @click="pickDifferentTeam"
              class="btn btn-default"
              data-test="change-team-button"
              style="border-radius: 0 20px 20px 0; left: -1px"
              >{{ currentTeam.name }} <span class="caret"></span
            ></a>
          </li>
          <li class="jyre-logo-container">
            <a href="/home">
              <img
                src="../../assets/brand/gyre-logo-full-pill.png"
                alt="Gyre"
                class="jyre-top-nav-logo"
              />
            </a>
          </li>
        </ul>
      </div>

      <ul class="nav navbar-nav navbar-right">
        <li class="dropdown notifications">
          <UserNotificationsDropdown />
        </li>
        <SearchBar v-if="canSearch" :side-nav-is-open="sideNavIsOpen" />
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from "vue";
import Debug from "debug";
import UserNotificationsDropdown from "../notifications/user-notifications-dropdown.vue";
import { UserSelfDocumentApiModel } from "../../api-models";
import SearchBar from "../search-bar.vue";
import { csContext } from "../../services/cs-context";
import TeamPickerDialog from "../team-picker-dialog.vue";

const debug = Debug("cs.Navbar");

const props = defineProps<{
  model: UserSelfDocumentApiModel;
  sideNavIsOpen: boolean;
}>();

const teamPickerDialog = ref<InstanceType<typeof TeamPickerDialog>>();

onMounted(() => {
  debug("Mounted", props.model);
});

const currentTeam = computed(() => csContext.currentTeam);

const canCreateTeams = computed(() => props.model.platformPermissions.includes("CreateTeams"));

function pickDifferentTeam(): void {
  teamPickerDialog.value?.open();
}

const canSearch = computed(() => {
  return !props.model.platformPermissions.includes("AdministerPlatform");
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

.jyre-top-nav-logo {
  height: @navbar-height;
}

.navbar {
  .side-nav-toggle-btn:hover {
    .burger-menu,
    .burger-menu::before,
    .burger-menu::after {
      background: @navbar-default-link-hover-color;
    }
  }

  .jyre-logo-container {
    position: absolute;
    left: 0;
    display: none;
    justify-content: center;
    width: 100vw;

    a {
      padding: 0;
    }
  }

  .team-button-container {
    height: @navbar-height;
    display: flex;
    align-items: center;
    justify-content: center;

    .caret {
      transform: translateY(-1px);
      margin-left: 2px;
    }

    .btn {
      padding: 5px 12px;
      font-weight: bold;
      font-size: 12px;
      color: #8f8f8f;
      position: relative;
      background-color: white;

      &:hover,
      &:focus {
        z-index: 1;
        border-color: #dddddd;
        background-color: white;
      }
    }
  }

  @media (max-width: 375px) {
    .jyre-top-nav-logo {
      margin-right: 20px;
    }
  }

  .team-dropdown {
    z-index: 1;

    & > .dropdown-menu > li:not(:nth-last-child(-n + 3)) {
      margin-bottom: 6px;
    }

    & > .dropdown-menu > li > a {
      gap: 3px;
      display: flex;
      align-items: center;
      height: 30px;
    }

    @media (max-width: 685px) {
      & > .dropdown-menu {
        max-width: 90vw;
      }

      & > .dropdown-menu > li > a {
        white-space: normal;
      }
    }
  }

  @media (max-width: @screen-xxs-max) {
    li.team-dropdown,
    .team-button-container {
      display: none;
    }

    .jyre-logo-container {
      display: flex;
    }

    button.side-nav-toggle-btn {
      display: block;
    }
  }

  @media (min-width: @screen-sm) {
    .notifications {
      margin-right: 15px;
    }
  }

  .explicit-show-jyre-logo {
    display: flex;
  }
}

.current-team-name-container {
  display: flex;
  margin-left: 5px;
}

@media (max-width: @screen-xs-max) {
  .current-team-name {
    margin: 0 2px;
    display: inline-block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 45vw;
  }
}

.navbar .dropdown-image-container {
  height: 30px;
  width: 30px;
  margin-right: 2px;

  img {
    height: 100%;
    width: 100%;
    object-fit: contain;
  }
}

.dropdown-text-wrap {
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 60vw;
  display: inline-block;
  white-space: nowrap;

  @media (max-width: @screen-sm-max) {
    max-width: 45vw;
  }
}

.navbar .notifications {
  padding: 10px;
  margin-right: 0;
}
</style>
